import type { Incident, IncidentKind, IncidentsFile } from '../../shared/types';
import type { RecordBoard, RecordRow } from './records';

/**
 * Who has rostered the most players with off-field problems.
 *
 * The ledger this reads is APPEND-ONLY and starts at its own `observedFrom`
 * date, because Sleeper publishes no suspension history: `/players/nfl` is a
 * snapshot that gets overwritten, so there is no past to reconstruct and the
 * only honest count is one that begins the day we started writing it down.
 * Every caption on these numbers has to carry that window -- `windowLabel`
 * exists so no page has to spell it out itself and get it subtly different.
 *
 * Counting is by DISTINCT PLAYER, not by row and not by day. A manager who
 * held one suspended player for eleven weeks has had one problem, not eleven,
 * and `daysSeen` is a duration that belongs beside a name rather than inside a
 * total.
 */
export interface IncidentTally {
  userId: string;
  /** Distinct players, which is what the boards rank on. */
  count: number;
  byKind: Record<IncidentKind, number>;
  /** Newest first, so a card or a tooltip can name the recent ones. */
  players: Incident[];
  /** The most recent season this manager had one, for a board row's date. */
  season: string;
}

const emptyKinds = (): Record<IncidentKind, number> => ({
  suspension: 0,
  holdout: 0,
  personal: 0,
});

/**
 * One tally per manager who appears in the ledger, most incidents first.
 *
 * Managers with none are absent rather than present at zero. A board of ten
 * rows where nine read 0 says nothing, and the leader is the only claim here.
 */
export function incidentTallies(file: IncidentsFile | null, season?: string): IncidentTally[] {
  const rows = (file?.incidents ?? []).filter((i) => !season || i.season === season);
  const byUser = new Map<string, IncidentTally>();

  for (const i of rows) {
    let t = byUser.get(i.userId);
    if (!t) {
      t = { userId: i.userId, count: 0, byKind: emptyKinds(), players: [], season: i.season };
      byUser.set(i.userId, t);
    }
    t.players.push(i);
    t.byKind[i.kind] += 1;
    if (Number(i.season) > Number(t.season)) t.season = i.season;
  }

  // A player who is suspended in one season and holds out in the next is two
  // rows and two problems; the same player flagged twice in ONE season is
  // already one row, because the ledger keys on (player, manager, kind,
  // season). So the distinct count is per player-season, which is what
  // "players they have had trouble with" means over a multi-year career.
  for (const t of byUser.values()) {
    t.count = t.players.length;
    t.players.sort((a, b) => b.lastSeen.localeCompare(a.lastSeen) || a.name.localeCompare(b.name));
  }

  return [...byUser.values()].sort((a, b) => b.count - a.count || a.userId.localeCompare(b.userId));
}

/**
 * Board rows for the home page's all-time card.
 *
 * **`detail` is deliberately left undefined, and putting the player names there
 * would be a silent bug.** `topHighlights` counts co-holders by matching the
 * ranked value AND the rendered detail -- it has to, because `best-record`
 * sorts on win rate while printing a record. This board prints exactly what it
 * ranks, so a per-manager detail string would make two managers tied at 2
 * compare unequal, and the card would name one of them and drop the "and N
 * more" line entirely. That failure is invisible: the headline stays right.
 * A count board is precisely where ties are likely, so the risk is not
 * theoretical -- see the same warning on `win-streak` in CLAUDE.md.
 */
export function incidentRows(file: IncidentsFile | null): RecordRow[] {
  return incidentTallies(file).map((t) => ({
    userId: t.userId,
    value: t.count,
    season: t.season,
    week: null,
    isPlayoff: false,
  }));
}

/**
 * The window every count off this file is bounded by.
 *
 * **Two different claims, and printing the wrong one is a lie the page cannot
 * be caught in.** With only detector rows the bound is exact: nothing before
 * `observedFrom` was looked at, so `Since Aug 2026` is true. The moment a
 * BACKFILLED row lands the file escapes that bound -- a 2025 suspension sitting
 * under a "Since Aug 2026" caption reads as a contradiction at best and as a
 * misdated incident at worst. So a ledger holding manual rows reports the
 * earliest season it covers instead, and says `Recorded` rather than `Since`:
 * a hand-entered history is a record of what someone remembered, never a
 * systematic sweep, and the weaker verb is the honest one.
 */
export function windowLabel(file: IncidentsFile | null): string {
  const rows = file?.incidents ?? [];
  const manual = rows.filter((i) => i.source === 'manual');

  if (manual.length > 0) {
    const earliest = rows.reduce((min, i) => (Number(i.season) < Number(min) ? i.season : min), rows[0].season);
    return `Recorded from ${earliest}`;
  }

  if (!file?.observedFrom) return 'Since sync began';
  const d = new Date(`${file.observedFrom}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return 'Since sync began';
  return `Since ${d.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' })}`;
}

const lowerFirst = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);

export function incidentBoard(file: IncidentsFile | null): RecordBoard {
  return {
    key: 'off-field',
    title: 'Most Off-Field Incidents',
    // Only the first CHARACTER is lowered, computed rather than assumed.
    // `toLowerCase()` on the whole label takes the month with it and reads
    // "since sep 2025"; hard-coding the leading `s` was worse still, and read
    // "secorded from 2025" the moment `windowLabel` gained its second form.
    hint: `Suspensions, holdouts and personal absences — ${lowerFirst(windowLabel(file))}`,
    kind: 'count',
    rows: incidentRows(file).slice(0, 5),
  };
}

/**
 * The manager with the most incidents in one season, or null if nobody leads.
 *
 * **Null on a tie, which is where this differs from every other badge on
 * `/managers`.** The others rank continuous quantities -- points to the
 * hundredth, an efficiency ratio -- where two managers landing on the same
 * number is a freak event, so they break ties on iteration order and note that
 * deterministic beats arbitrary. This ranks small integers, where a tie at the
 * top is the ordinary case rather than the freak one, and a badge prints no
 * number: two managers on 2 would show one badge and no way to tell it was a
 * coin flip. Withholding it is the same rule `TradeRecord.spread` and
 * `buildPreview` already follow -- say nothing rather than assert a winner the
 * data does not pick.
 */
export function seasonIncidentLeader(file: IncidentsFile | null, season: string): string | null {
  const tallies = incidentTallies(file, season);
  if (tallies.length === 0) return null;
  if (tallies.length > 1 && tallies[1].count === tallies[0].count) return null;
  return tallies[0].userId;
}
